/**
 * Subscription store for managing subscription state.
 * Uses Zustand for simple state management.
 */

import { create } from 'zustand';
import { subscriptionService, SubscriptionResponse } from '@/api/services/subscription-service';

interface PortalCache {
  url: string;
  expiresAt: number;
}

interface SubscriptionState {
  // State
  subscription: SubscriptionResponse | null;
  isLoading: boolean;
  error: string | null;
  portalCache: PortalCache | null;

  // Actions
  fetchSubscription: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
  setPortalCache: (url: string) => void;
  getPortalCache: () => string | null;
}

const PORTAL_CACHE_DURATION = 50 * 60 * 1000; // 50 minutes in milliseconds

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  // Initial state
  subscription: null,
  isLoading: false,
  error: null,
  portalCache: null,

  // Actions
  fetchSubscription: async () => {
    // Don't fetch if already loading
    if (get().isLoading) return;

    // Don't fetch if we already have subscription data
    if (get().subscription !== null) return;

    set({ isLoading: true, error: null });

    try {
      const subscription = await subscriptionService.getSubscription();
      set({ subscription, isLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch subscription';
      set({ error: errorMessage, isLoading: false });
    }
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set({ subscription: null, isLoading: false, error: null, portalCache: null });
  },

  setPortalCache: (url: string) => {
    set({
      portalCache: {
        url,
        expiresAt: Date.now() + PORTAL_CACHE_DURATION,
      },
    });
  },

  getPortalCache: () => {
    const cache = get().portalCache;
    if (!cache) return null;

    // Check if cache has expired
    if (Date.now() > cache.expiresAt) {
      set({ portalCache: null });
      return null;
    }

    return cache.url;
  },
}));

// Selectors for common use cases
export const useSubscriptionStatus = () => {
  const subscription = useSubscriptionStore((state) => state.subscription);
  return {
    isActive: subscription?.is_active ?? false,
    hasSubscription: subscription !== null,
  };
};

export const useSubscriptionLoading = () => {
  return useSubscriptionStore((state) => state.isLoading);
};

export const useSubscriptionError = () => {
  return useSubscriptionStore((state) => state.error);
};
